import React, { useEffect, useState } from "react";
import { Alert, Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import { ApiUrl } from "../../ApiUrl";
import "./ForgotPasswordModal.css";

const initialForm = {
  username: "",
  otp: "",
  newPassword: "",
  confirmPassword: "",
};

const parseResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }
  return { message: "The server could not process this request." };
};

const ForgotPasswordModal = ({ show, onHide }) => {
  const [form, setForm] = useState(initialForm);
  const [otpSent, setOtpSent] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!show) {
      setForm(initialForm);
      setOtpSent(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setLoading(false);
      setError("");
      setMessage("");
      setCompleted(false);
    }
  }, [show]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setError("");
  };

  const sendCode = async () => {
    if (!form.username.trim()) {
      setError("Please enter your username.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");
    try {
      const response = await fetch(`${ApiUrl.apiurl}auth/password-reset/request/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: form.username.trim() }),
      });
      const data = await parseResponse(response);
      if (!response.ok) {
        throw new Error(data?.message || "Unable to send the reset code.");
      }
      setOtpSent(true);
      setMessage(data.message || "A reset code was sent to your email address.");
    } catch (requestError) {
      setError(requestError.message || "Unable to send the reset code.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (!/^\d{6}$/.test(form.otp.trim())) {
      setError("Please enter the 6-digit OTP.");
      return;
    }
    if (!form.newPassword) {
      setError("Please enter a new password.");
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError("New password and confirm password do not match.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${ApiUrl.apiurl}auth/password-reset/confirm/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: form.username.trim(),
          otp: form.otp.trim(),
          new_password: form.newPassword,
          confirm_password: form.confirmPassword,
        }),
      });
      const data = await parseResponse(response);
      if (!response.ok) {
        throw new Error(data?.message || "Unable to reset the password.");
      }
      setCompleted(true);
      setMessage(data.message || "Password reset successfully.");
    } catch (requestError) {
      setError(requestError.message || "Unable to reset the password.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (completed) {
      onHide();
    } else if (otpSent) {
      resetPassword();
    } else {
      sendCode();
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="forgot-password-dialog"
      contentClassName="forgot-password-content"
      aria-labelledby="forgot-password-title"
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className="forgot-password-header">
          <Modal.Title id="forgot-password-title">Forgot Password</Modal.Title>
        </Modal.Header>

        <Modal.Body className="forgot-password-body">
          {error && <Alert variant="danger">{error}</Alert>}
          {message && <Alert variant="success">{message}</Alert>}

          <Form.Group className="forgot-password-field">
            <Form.Label>User Name</Form.Label>
            <Form.Control
              name="username"
              value={form.username}
              onChange={updateField}
              placeholder="Enter User Name"
              disabled={otpSent || completed || loading}
              autoComplete="username"
            />
          </Form.Group>

          <Form.Group className="forgot-password-field">
            <div className="d-flex align-items-center justify-content-between">
              <Form.Label>OTP</Form.Label>
              {otpSent && !completed && (
                <button
                  type="button"
                  className="forgot-password-resend"
                  onClick={sendCode}
                  disabled={loading}
                >
                  Resend Code
                </button>
              )}
            </div>
            <Form.Control
              name="otp"
              value={form.otp}
              onChange={updateField}
              placeholder="Enter OTP"
              disabled={!otpSent || completed || loading}
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
            />
          </Form.Group>

          <Form.Group className="forgot-password-field">
            <Form.Label>New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showNewPassword ? "text" : "password"}
                name="newPassword"
                value={form.newPassword}
                onChange={updateField}
                disabled={!otpSent || completed || loading}
                autoComplete="new-password"
              />
              <Button
                type="button"
                variant="outline-secondary"
                onClick={() => setShowNewPassword((visible) => !visible)}
                disabled={!otpSent || completed}
                aria-label={showNewPassword ? "Hide new password" : "Show new password"}
              >
                {showNewPassword ? <BsEyeSlash /> : <BsEye />}
              </Button>
            </InputGroup>
          </Form.Group>

          <Form.Group className="forgot-password-field mb-0">
            <Form.Label>Confirm Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={updateField}
                disabled={!otpSent || completed || loading}
                autoComplete="new-password"
              />
              <Button
                type="button"
                variant="outline-secondary"
                onClick={() => setShowConfirmPassword((visible) => !visible)}
                disabled={!otpSent || completed}
                aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              >
                {showConfirmPassword ? <BsEyeSlash /> : <BsEye />}
              </Button>
            </InputGroup>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="forgot-password-footer">
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Close
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading && <Spinner animation="border" size="sm" className="me-2" />}
            {completed ? "Done" : otpSent ? "Reset Password" : "Send Code"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ForgotPasswordModal;
